import { logError } from '@ptek/client-logger';
import { useCallback } from 'react';
import {
  DIALOG_TYPES,
  EXIT_ACTIONS,
  ExitActionType,
  useDialogStore,
} from '../../../stores/dialogStore';

interface UseExitFlowProps {
  isCurrentlyRecording?: boolean;
  onSaveAndExit?: (exitAction: 'logout' | 'close') => Promise<void>;
  onConfirmClose?: () => void;
  onResetStore?: () => void;
}

export const useExitFlow = (props: UseExitFlowProps = {}) => {
  const {
    isCurrentlyRecording = false,
    onSaveAndExit,
    onConfirmClose,
    onResetStore,
  } = props;
  const { openDialog } = useDialogStore();

  const handleLogout = useCallback(async (): Promise<void> => {
    if (isCurrentlyRecording && onSaveAndExit) {
      try {
        const result = await openDialog<{
          confirmed: boolean;
          action?: ExitActionType;
        }>(DIALOG_TYPES.EXIT_CONFIRM, {
          action: EXIT_ACTIONS.LOGOUT,
        });

        if (result.confirmed) {
          await onSaveAndExit('logout');
        }
      } catch (error) {
        logError('useExitFlow', 'Error showing logout dialog:', error);
      }
    } else {
      if (onResetStore) {
        onResetStore();
      }
    }
  }, [isCurrentlyRecording, openDialog, onSaveAndExit, onResetStore]);

  const handleBeforeClose = useCallback(
    async (isUnmount: boolean): Promise<boolean> => {
      if (isCurrentlyRecording && onSaveAndExit) {
        try {
          const result = await openDialog<{
            confirmed: boolean;
            action?: ExitActionType;
          }>(DIALOG_TYPES.EXIT_CONFIRM, {
            action: EXIT_ACTIONS.CLOSE,
          });

          if (result.confirmed) {
            await onSaveAndExit('close');
          }
        } catch (error) {
          logError('useExitFlow', 'Error showing before close dialog:', error);
          onConfirmClose?.();
        }
        return false;
      } else {
        onConfirmClose?.();
        if (isUnmount && onResetStore) {
          onResetStore();
        }
        return true;
      }
    },
    [
      isCurrentlyRecording,
      openDialog,
      onSaveAndExit,
      onConfirmClose,
      onResetStore,
    ]
  );

  return {
    handleLogout,
    handleBeforeClose,
  };
};
